import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Save, Settings2 } from 'lucide-react';
import { api, extractError } from '../../lib/api';
import { useConfirm } from '../../lib/confirm';
import { fmtDate } from '../../lib/date';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';

const WD = [{ v: 0, l: 'Mo' }, { v: 1, l: 'Di' }, { v: 2, l: 'Mi' }, { v: 3, l: 'Do' }, { v: 4, l: 'Fr' }, { v: 5, l: 'Sa' }, { v: 6, l: 'So' }];

export default function PolicyAssignments({ costCenters, canEdit, reloadKey }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState({ items: [], policies: [] });
  const [loading, setLoading] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [overrideDrafts, setOverrideDrafts] = useState({});
  const { confirm } = useConfirm();

  const kstById = useMemo(() => Object.fromEntries(costCenters.map(k => [k.id, k])), [costCenters]);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get('/policy-assignments', { params: { year } }); setData(r.data); setOverrideDrafts({}); }
    catch (err) { toast.error(extractError(err)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, reloadKey]);

  const assignPolicy = async (employeeId, policyId) => {
    try { await api.post('/policy-assignments', { employee_id: employeeId, policy_id: policyId }); toast.success('Richtlinie zugeordnet.'); load(); }
    catch (err) { toast.error(extractError(err)); }
  };

  const saveOverride = async (employeeId, value) => {
    const raw = value === '' || value === undefined ? null : parseFloat(String(value).replace(',', '.'));
    if (raw !== null && Number.isNaN(raw)) { toast.error('Ungültiger Wert.'); return; }
    try {
      await api.post(`/policy-assignments/${employeeId}/entitlement-override`, { year, days: raw });
      toast.success(raw === null ? 'Zurück auf Automatik.' : 'Anspruch manuell gesetzt.');
      load();
    } catch (err) { toast.error(extractError(err)); }
  };

  const deletePolicy = async (p) => {
    if (!(await confirm({ title: `Richtlinie „${p.name}" löschen?`, variant: 'destructive', confirmLabel: 'Löschen' }))) return;
    try { await api.delete(`/absence-policies/${p.id}`); toast.success('Richtlinie gelöscht.'); load(); }
    catch (err) { toast.error(extractError(err)); }
  };

  return (
    <div className="space-y-4">
      {/* Richtlinien verwalten */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="font-display text-base flex items-center gap-2"><Settings2 size={16} />Richtlinien</CardTitle>
          {canEdit && <Button size="sm" onClick={() => setEditingPolicy({ name: '', color: '#0284c7', hours_per_day: 8, work_weekdays: [0, 1, 2, 3, 4], annual_vacation_days: 30, carryover_enabled: true, carryover_max_days: 30, carryover_expire_month: 3, carryover_expire_day: 31, active: true })} data-testid="policy-add"><Plus size={14} className="mr-1" />Richtlinie</Button>}
        </CardHeader>
        <CardContent>
          <div className="border rounded-md overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Richtlinie</TableHead>
                <TableHead className="text-right">Urlaubstage/Jahr</TableHead>
                <TableHead className="text-right">Std./Tag</TableHead>
                <TableHead>Arbeitstage</TableHead>
                <TableHead>Übertrag</TableHead>
                <TableHead className="text-right">Aktion</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.policies.map(p => (
                  <TableRow key={p.id} data-testid={`policy-row-${p.id}`}>
                    <TableCell><span className="inline-flex items-center gap-2 font-semibold"><span className="w-3 h-3 rounded-sm" style={{ background: p.color }} />{p.name}</span></TableCell>
                    <TableCell className="text-right font-mono">{p.annual_vacation_days}</TableCell>
                    <TableCell className="text-right font-mono">{p.hours_per_day}</TableCell>
                    <TableCell><div className="flex gap-1">{WD.map(w => <span key={w.v} className={`text-[10px] font-bold px-1 py-0.5 rounded ${(p.work_weekdays || []).includes(w.v) ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-400'}`}>{w.l}</span>)}</div></TableCell>
                    <TableCell className="text-xs text-slate-600">{p.carryover_enabled ? `max ${p.carryover_max_days} T, Verfall ${String(p.carryover_expire_day).padStart(2, '0')}.${String(p.carryover_expire_month).padStart(2, '0')}.` : 'Kein Übertrag'}</TableCell>
                    <TableCell className="text-right">
                      {canEdit && <>
                        <Button size="sm" variant="ghost" onClick={() => setEditingPolicy(p)} data-testid={`policy-edit-${p.id}`}><Edit2 size={14} /></Button>
                        <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => deletePolicy(p)} data-testid={`policy-delete-${p.id}`}><Trash2 size={14} /></Button>
                      </>}
                    </TableCell>
                  </TableRow>
                ))}
                {data.policies.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-6">Noch keine Richtlinien.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Zuordnungen */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="font-display text-base">Zuordnungen & Urlaubsanspruch</CardTitle>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-24 h-8 text-sm font-semibold" data-testid="assign-year"><SelectValue /></SelectTrigger>
            <SelectContent>{[year - 1, year, year + 1].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {/* Eine Richtlinie kann bewusst nicht entfernt werden, nur gewechselt.
              Grund: Ohne Zuordnung fällt die Berechnung still auf 8 Stunden und
              Montag bis Freitag zurück – bei einer Teilzeitkraft hätte das
              rückwirkend für alle Monate ein zu hohes Soll und ein negatives
              Saldo zur Folge, ohne dass irgendwo stünde warum. */}
          <p className="text-xs text-slate-500 mb-2">
            Jeder Mitarbeiter braucht eine Richtlinie – sie bestimmt Soll-Stunden, Arbeitstage und
            Urlaubsanspruch. Wer keine Sonderregelung hat, bekommt die Standard-Richtlinie.
            Entfernen ist nicht möglich, nur wechseln: Ohne Zuordnung würde still mit 8 Stunden und
            Montag–Freitag gerechnet, und vergangene Monate würden sich rückwirkend ändern.
          </p>
          <div className="border rounded-md overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Kostenstelle</TableHead>
                <TableHead>Eintritt</TableHead>
                <TableHead>Richtlinie</TableHead>
                <TableHead className="text-right">Anspruch automatisch</TableHead>
                <TableHead className="text-right">Anspruch manuell</TableHead>
                <TableHead className="text-right">Effektiv {year}</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.items.map(it => {
                  const kst = kstById[it.cost_center_id];
                  const draftKey = it.employee_id;
                  const draftVal = overrideDrafts[draftKey] !== undefined ? overrideDrafts[draftKey] : (it.override_entitlement ?? '');
                  return (
                    <TableRow key={it.employee_id} data-testid={`assign-row-${it.employee_id}`}>
                      <TableCell className="font-semibold whitespace-nowrap">{it.employee_name}</TableCell>
                      <TableCell className="text-sm text-slate-600">{kst?.name || '—'}</TableCell>
                      <TableCell className="font-mono text-xs">{it.start_date ? fmtDate(it.start_date) : '—'}</TableCell>
                      <TableCell>
                        {canEdit ? (
                          <Select value={it.policy_id || ''} onValueChange={(v) => assignPolicy(it.employee_id, v)}>
                            <SelectTrigger
                              className={`w-44 h-8 text-sm ${it.policy_id ? '' : 'border-amber-400 bg-amber-50'}`}
                              data-testid={`assign-policy-${it.employee_id}`}>
                              <SelectValue placeholder="Noch keine – bitte wählen" />
                            </SelectTrigger>
                            <SelectContent>{data.policies.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : (it.policy_name || '—')}
                      </TableCell>
                      <TableCell className="text-right font-mono text-slate-500">{it.auto_entitlement != null ? it.auto_entitlement : '—'}</TableCell>
                      <TableCell className="text-right">
                        {canEdit ? (
                          <div className="flex items-center justify-end gap-1">
                            <Input value={draftVal} onChange={e => setOverrideDrafts({ ...overrideDrafts, [draftKey]: e.target.value })} placeholder="auto" className="w-20 h-8 text-sm text-right" data-testid={`assign-override-${it.employee_id}`} />
                            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => saveOverride(it.employee_id, draftVal)} title="Speichern"><Save size={13} /></Button>
                            {it.override_entitlement != null && <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" onClick={() => saveOverride(it.employee_id, '')} title="Zurück auf Automatik"><Trash2 size={13} /></Button>}
                          </div>
                        ) : (it.override_entitlement != null ? it.override_entitlement : '—')}
                      </TableCell>
                      <TableCell className="text-right font-mono font-bold">{it.entitlement != null ? it.entitlement : '—'}{it.override_entitlement != null && <Badge variant="outline" className="ml-1 bg-sky-50 text-sky-700 border-sky-200 text-[10px]">manuell</Badge>}</TableCell>
                    </TableRow>
                  );
                })}
                {!loading && data.items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">Keine Mitarbeiter.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <PolicyDialog open={!!editingPolicy} onOpenChange={(v) => !v && setEditingPolicy(null)} policy={editingPolicy} onSaved={load} />
    </div>
  );
}

function PolicyDialog({ open, onOpenChange, policy, onSaved }) {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (open && policy) setForm({ ...policy, work_weekdays: policy.work_weekdays || [0, 1, 2, 3, 4] }); }, [open, policy]);

  if (!form) return null;
  const isEdit = !!form.id;
  const toggleWd = (v) => { const s = new Set(form.work_weekdays); s.has(v) ? s.delete(v) : s.add(v); setForm({ ...form, work_weekdays: [...s].sort((a, b) => a - b) }); };

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Name ist Pflicht.'); return; }
    setLoading(true);
    const payload = {
      name: form.name.trim(), color: form.color, hours_per_day: Number(form.hours_per_day),
      work_weekdays: form.work_weekdays, annual_vacation_days: Number(form.annual_vacation_days),
      carryover_enabled: !!form.carryover_enabled, carryover_max_days: Number(form.carryover_max_days),
      carryover_expire_month: Number(form.carryover_expire_month), carryover_expire_day: Number(form.carryover_expire_day),
    };
    try {
      if (isEdit) await api.patch(`/absence-policies/${form.id}`, payload);
      else await api.post('/absence-policies', payload);
      toast.success(isEdit ? 'Richtlinie aktualisiert.' : 'Richtlinie angelegt.');
      onSaved?.(); onOpenChange(false);
    } catch (err) { toast.error(extractError(err)); } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="policy-dialog">
        <DialogHeader><DialogTitle>{isEdit ? 'Richtlinie bearbeiten' : 'Richtlinie anlegen'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="policy-name" /></div>
            <div><Label>Farbe</Label><Input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} className="h-10 p-1" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Sollstunden / Tag</Label><Input type="number" step="0.5" value={form.hours_per_day} onChange={e => setForm({ ...form, hours_per_day: e.target.value })} data-testid="policy-hours" /></div>
            <div><Label>Urlaubstage / Jahr</Label><Input type="number" step="0.5" value={form.annual_vacation_days} onChange={e => setForm({ ...form, annual_vacation_days: e.target.value })} data-testid="policy-vacation" /></div>
          </div>
          <div>
            <Label>Arbeitstage (zählen zu möglichen Stunden)</Label>
            <div className="flex gap-1.5 mt-1.5">
              {WD.map(w => <button key={w.v} type="button" onClick={() => toggleWd(w.v)} className={`flex-1 py-2 rounded border text-sm font-bold transition-colors ${form.work_weekdays.includes(w.v) ? 'bg-slate-800 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}>{w.l}</button>)}
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Hinweis: Samstag zählt nur zu den möglichen Stunden, wenn tatsächlich gearbeitet wurde.</p>
          </div>
          <label className="flex items-center gap-2 text-sm font-semibold">
            <input type="checkbox" checked={form.carryover_enabled} onChange={e => setForm({ ...form, carryover_enabled: e.target.checked })} data-testid="policy-carryover" />
            Resturlaub ins Folgejahr übertragen
          </label>
          {form.carryover_enabled && (
            <div className="grid grid-cols-3 gap-2 pl-6">
              <div><Label className="text-xs">Max. Tage</Label><Input type="number" step="0.5" value={form.carryover_max_days} onChange={e => setForm({ ...form, carryover_max_days: e.target.value })} /></div>
              <div><Label className="text-xs">Verfall Tag</Label><Input type="number" min="1" max="31" value={form.carryover_expire_day} onChange={e => setForm({ ...form, carryover_expire_day: e.target.value })} /></div>
              <div><Label className="text-xs">Verfall Monat</Label><Input type="number" min="1" max="12" value={form.carryover_expire_month} onChange={e => setForm({ ...form, carryover_expire_month: e.target.value })} /></div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={submit} disabled={loading} data-testid="policy-submit">{isEdit ? 'Speichern' : 'Anlegen'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
